import type { SemanticTone } from '../../components/ui';
import type {
  AlertKind,
  SignalCategory,
  SignalSeverity,
  SignalStatus,
  WatchFrequency,
} from '../../types/monitoring';

/* ------------------------------------------------------------------ */
/* Labels                                                              */
/* ------------------------------------------------------------------ */

export const SEVERITIES: SignalSeverity[] = ['critical', 'high', 'medium', 'low'];

export const SEVERITY_LABEL: Record<SignalSeverity, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

/** Most severe first, so a list sorted by this reads as a priority queue */
export const SEVERITY_RANK: Record<SignalSeverity, number> = { critical: 0, high: 1, medium: 2, low: 3 };

/**
 * Severity earns colour because it answers "is this OK?". Critical and high are
 * the brand red (danger), medium amber, low stays neutral — the word carries the
 * rest, so the pills read without relying on colour.
 */
export const severityTone = (severity: SignalSeverity): SemanticTone =>
  severity === 'critical' || severity === 'high' ? 'red' : severity === 'medium' ? 'amber' : 'neutral';

export const CATEGORIES: SignalCategory[] = [
  'appointment',
  'departure',
  'legal',
  'regulatory',
  'protest',
  'financial',
  'corruption',
  'partnership',
  'statement',
  'security',
  'other',
];

export const CATEGORY_LABEL: Record<SignalCategory, string> = {
  appointment: 'Appointment',
  departure: 'Departure',
  legal: 'Legal',
  regulatory: 'Regulatory',
  protest: 'Protest',
  financial: 'Financial',
  corruption: 'Corruption',
  partnership: 'Partnership',
  statement: 'Statement',
  security: 'Security',
  other: 'Other',
};

export const SIGNAL_STATUS_LABEL: Record<SignalStatus, string> = {
  new: 'New',
  reviewed: 'Reviewed',
  dismissed: 'Dismissed',
  escalated: 'Escalated',
};

export const signalStatusTone = (status: SignalStatus): SemanticTone =>
  status === 'escalated' ? 'red' : status === 'reviewed' ? 'green' : status === 'dismissed' ? 'neutral' : 'blue';

export const ALERT_KIND_LABEL: Record<AlertKind, string> = {
  sentiment_drop: 'Sentiment drop',
  volume_spike: 'Volume spike',
  high_severity: 'Severe signal',
  role_change: 'Role change',
  co_mention: 'Co-mention',
  going_dark: 'Gone quiet',
};

export const FREQUENCY_LABEL: Record<WatchFrequency, string> = {
  daily: 'Daily',
  weekly: 'Weekly',
  paused: 'Paused',
};

export const FREQUENCIES: WatchFrequency[] = ['daily', 'weekly', 'paused'];

/** Names of the numbers alerts put in `evidence.metrics`, so the row reads in English */
export const METRIC_LABEL: Record<string, string> = {
  before: 'Before',
  after: 'After',
  delta: 'Change',
  baseline: 'Baseline',
  recent: 'Recent',
  factor: 'Multiple',
  count: 'Signals',
  days: 'Days',
  sentimentBefore: 'Sentiment before',
  sentimentAfter: 'Sentiment after',
  daysSinceLastSignal: 'Days quiet',
};

export const metricLabel = (key: string): string =>
  METRIC_LABEL[key] || key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase());

/* ------------------------------------------------------------------ */
/* Sentiment                                                           */
/* ------------------------------------------------------------------ */

export type SentimentBand = 'negative' | 'neutral' | 'positive';

/** −1 … +1, read in three bands so the word (not the colour) carries the meaning */
export function sentimentBand(value: number): SentimentBand {
  if (value <= -0.2) return 'negative';
  if (value >= 0.2) return 'positive';
  return 'neutral';
}

export const SENTIMENT_LABEL: Record<SentimentBand, string> = {
  negative: 'Negative',
  neutral: 'Neutral',
  positive: 'Positive',
};

/* ------------------------------------------------------------------ */
/* Dates                                                               */
/* ------------------------------------------------------------------ */

const DAY = 86_400_000;

/** "4h ago", "3d ago", then a plain date. Empty string for null, never "Invalid Date". */
export function timeAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = now - then;
  if (diff < 0) return formatDate(iso);
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(diff / DAY);
  if (days < 7) return `${days}d ago`;
  return formatDate(iso);
}

/** "12 Mar 2026" style short date */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Next scan in 3h", or "overdue" when the moment has passed */
export function timeUntil(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = then - now;
  if (diff <= 0) return 'due now';
  const hours = Math.round(diff / 3_600_000);
  if (hours < 1) return 'in under an hour';
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.round(diff / DAY)}d`;
}

/** The window an alert covers, in one short phrase */
export function windowLabel(start: string | null, end: string | null): string {
  if (!start && !end) return '';
  if (start && end) {
    const days = Math.max(1, Math.round((new Date(end).getTime() - new Date(start).getTime()) / DAY));
    return `${formatDate(start)} – ${formatDate(end)} · ${days}d`;
  }
  return formatDate(start || end);
}

/* ------------------------------------------------------------------ */
/* Links                                                               */
/* ------------------------------------------------------------------ */

export const subjectHref = (subject: { type: string; id: string }): string =>
  subject.type === 'organization' ? `/organizations/${subject.id}` : `/stakeholders/${subject.id}`;

/** "lemonde.fr" from a full article URL, for a compact source line */
export function hostOf(url: string | null | undefined): string {
  if (!url) return '';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return '';
  }
}
